import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AttributeKindEnum } from "@brewbase/db/types";
import InlineEditableInput from "@/components/InlineEditableInput";
import { CodeGenerateButton } from "@/components/code-generate-button";
import { toCodeIdentifier } from "@/lib/utils";
import {
  AttributeKindSelector,
  kindOptions as defaultKindOptions,
} from "@/components/template-attribute-item/AttributeKindSelector";
import { Trash2Icon } from "lucide-react";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import { SectionHeader, TypeSettingsContent } from "./inspector-helpers";
import type { BaseTemplateAttribute } from "./types";
import { useEditorConfig } from "./editor-config-context";
import { AnalytePicker as PublicAnalytePicker } from "@/components/analyte-picker";

interface InspectorScalarProps {
  attribute: BaseTemplateAttribute;
  templateId: number;
  isLocked: boolean;
  onUpdateAttributeType: (id: number, updates: Record<string, unknown>) => void;
  onUpdateSettings: (id: number, settings: Record<string, unknown>) => void;
  onRemove: (id: number) => void;
}

export function InspectorScalar({
  attribute,
  templateId,
  isLocked: templateLocked,
  onUpdateAttributeType,
  onUpdateSettings,
  onRemove,
}: InspectorScalarProps) {
  const {
    formulaContextKind,
    renderInspectorExtras,
    kindOptions = defaultKindOptions,
    renderTypeSettings,
    showDescription = true,
    isGeneratedAttribute,
  } = useEditorConfig();
  // A generated field's own settings are shown, never edited.
  const isGenerated = isGeneratedAttribute?.(attribute) ?? false;
  const isLocked = templateLocked || isGenerated;
  const kind = attribute.kind as AttributeKindEnum;

  const handleTypeUpdate = (updates: Record<string, unknown>) =>
    onUpdateAttributeType(attribute.id, updates);

  const handleSettingsUpdate = (settings: Record<string, unknown>) =>
    onUpdateSettings(attribute.id, settings);

  return (
    <div className="space-y-0">
      {/* Identity */}
      <SectionHeader title="Identity" />
      <div className="px-3 py-3 space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-medium">Name</Label>
            <InlineEditableInput
              autoFocus={attribute.name === ""}
              value={attribute.name || ""}
              onSave={(next) => handleTypeUpdate({ name: next })}
              placeholder="Attribute name"
              disabled={isLocked}
              inputClassName="h-7 text-sm mt-1"
            />
          </div>
          <div>
            <Label className="text-xs font-medium">Kind</Label>
            <AttributeKindSelector
              value={kind}
              onChange={(value) =>
                handleTypeUpdate({ kind: value as AttributeKindEnum })
              }
              disabled={isLocked}
              options={kindOptions}
            />
          </div>
        </div>
        {showDescription && (
          <div>
            <Label className="text-xs font-medium">Description</Label>
            <InlineEditableInput
              value={attribute.description || ""}
              onSave={(next) => handleTypeUpdate({ description: next })}
              placeholder="Click to edit"
              disabled={isLocked}
              inputClassName="h-7 mt-1"
            />
          </div>
        )}
        <div>
          <Label className="text-xs font-medium">Code</Label>
          <div className="flex items-start gap-1 mt-1">
            <InlineEditableInput
              className="flex-1"
              value={attribute.code || ""}
              onSave={(next) =>
                handleTypeUpdate({ code: toCodeIdentifier(next) || null })
              }
              placeholder="e.g. wet_weight"
              disabled={isLocked}
              inputClassName="h-7 text-sm font-mono"
            />
            <CodeGenerateButton
              sourceName={attribute.name ?? ""}
              onGenerate={(code) => handleTypeUpdate({ code })}
              disabled={isLocked}
            />
          </div>
          {formulaContextKind !== undefined && formulaContextKind !== "harvest-attribute" && !isLocked && (
            <AnalytePicker
              onPick={(code) => handleTypeUpdate({ code })}
              current={attribute.code ?? null}
            />
          )}
        </div>
      </div>

      {/* Type Settings */}
      {kind && (
        <>
          <SectionHeader
            title={`${kindOptions.find((k) => k.value === kind)?.label ?? kind} Settings`}
          />
          <div className="px-3 py-3">
            {isGenerated ? (
              <GeneratedTypeSettings attribute={attribute} />
            ) : renderTypeSettings ? (
              renderTypeSettings({
                attribute,
                kind,
                isLocked,
                onUpdateAttributeType: handleTypeUpdate,
              })
            ) : formulaContextKind ? (
              <TypeSettingsContent
                kind={kind}
                attribute={attribute}
                templateId={templateId}
                formulaContextKind={formulaContextKind}
                onUpdateAttributeType={handleTypeUpdate}
              />
            ) : null}
          </div>
        </>
      )}

      {/* Entity-specific middle: flags, defaults, control-point / specification,
          mass balance */}
      {renderInspectorExtras({
        attribute,
        isLocked: templateLocked,
        isGenerated,
        onUpdateAttributeType: handleTypeUpdate,
        onUpdateSettings: handleSettingsUpdate,
      })}

      {/* Delete */}
      {!isLocked && (
        <div className="px-3 py-3 border-t border-border">
          <DeleteConfirmDialog
            title="Delete Attribute"
            description={`Are you sure you want to delete "${attribute.name || "Untitled"}"? This cannot be undone.`}
            onConfirm={() => onRemove(attribute.id)}
            trigger={
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10 text-xs"
              >
                <Trash2Icon className="h-3 w-3 mr-1" />
                Delete attribute
              </Button>
            }
          />
        </div>
      )}
    </div>
  );
}


/**
 * Bind a test attribute to an existing analyte instead of minting a new code.
 *
 * The registry is what makes an in-house measurement and an external lab's the
 * same quantity, and that only works if authors reach for the existing entry.
 * Free text is still allowed — the field above is unchanged — but a code
 * already in use should be one click, not a spelling exercise.
 */
function AnalytePicker({
  onPick,
  current,
}: {
  onPick: (code: string) => void;
  current: string | null;
}) {
  // Public analytes only: binding is a claim that this attribute measures
  // that quantity, and the server checks the unit and kind fit. A scalar
  // attribute cannot bind to a profile table.
  return (
    <div className="mt-1">
      <PublicAnalytePicker
        value={current}
        onChange={(code) => code && onPick(code)}
        placeholder="Bind to a catalogued result…"
        filter={(a) => a.kind !== AttributeKindEnum.TABULAR_DATA}
      />
    </div>
  );
}

/** The settings of a blueprint-generated field, read-only. */
function GeneratedTypeSettings({ attribute }: { attribute: BaseTemplateAttribute }) {
  const rows: [string, string | null | undefined, boolean][] = [
    ["Formula", attribute.computed_number_formula, true],
    ["Result unit", attribute.computed_number_result_base_unit, false],
    ["Unit", attribute.measurement_default_unit, false],
  ];
  return (
    <div className="flex flex-col gap-2">
      {rows
        .filter(([, value]) => value != null && value !== "")
        .map(([label, value, mono]) => (
          <div key={label} className="flex flex-col gap-0.5">
            <span className="text-xs text-olive-9">{label}</span>
            <span className={mono ? "font-mono text-sm break-words" : "text-sm"}>{value}</span>
          </div>
        ))}
      <p className="text-xs text-olive-9">
        Generated by the method&apos;s blueprint. It changes with the method&apos;s
        compounds, or when the blueprint does; its default is the method&apos;s own.
      </p>
    </div>
  );
}
